import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { clearToken, currentClaims } from "../lib/auth";
import { fmtDate, fmtDateTime } from "../lib/formatDates";
import { useEnterpriseBranding } from "../lib/useEnterpriseBranding";
import { EditProfileDrawer } from "../console/EditProfileDrawer";
import { DASH, useDHDistrict, useMe } from "./data";

export function Profile() {
  const navigate = useNavigate();
  const claims = currentClaims();
  const { enterprise } = useEnterpriseBranding();
  const meQ = useMe();
  const districtQ = useDHDistrict();
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const user = meQ.data?.user;
  const district = districtQ.data?.district;
  const email = user?.email || claims?.email || "";
  const fullName = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : email;
  const initials =
    (user
      ? (user.first_name[0] || "") + (user.last_name[0] || "")
      : email.slice(0, 2)
    ).toUpperCase() || "DH";

  let roleLine = "District head";
  if (district) roleLine += ` — ${district.name}`;
  if (enterprise) roleLine += ` · ${enterprise.name}`;

  const signOut = () => {
    clearToken();
    navigate("/login", { replace: true });
  };

  return (
    <>
      <p className="crumb">
        {(district?.name ?? "DISTRICT").toUpperCase()} / PROFILE
      </p>
      <div className="page-head">
        <div>
          <h1>Profile</h1>
          <p>Your details, your access scope, and how AgriLens reaches you.</p>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => setEditing(true)}>
            Edit details
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="prof-head">
          {user?.avatar_url ? (
            <span
              className="avatar-lg"
              style={{
                backgroundImage: `url(${user.avatar_url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "transparent",
              }}
            >
              {initials}
            </span>
          ) : (
            <span className="avatar-lg">{initials}</span>
          )}
          <div>
            <h2>{fullName || DASH}</h2>
            <p className="role">{roleLine || "District head"}</p>
          </div>
        </div>
        <dl className="dl">
          <div>
            <dt>EMPLOYEE ID</dt>
            <dd>{DASH}</dd>
          </div>
          <div>
            <dt>WORK EMAIL</dt>
            <dd>{email || DASH}</dd>
          </div>
          <div>
            <dt>MOBILE</dt>
            <dd>{user?.mobile || DASH}</dd>
          </div>
          <div>
            <dt>ACCESS SCOPE</dt>
            <dd>{district ? `${district.name} district only` : DASH}</dd>
          </div>
          <div>
            <dt>ROLE</dt>
            <dd>District head</dd>
          </div>
          <div>
            <dt>PERMISSIONS</dt>
            <dd>
              Adds godowns and their QC agents; views every report in the
              district.
            </dd>
          </div>
          <div>
            <dt>LAST SIGNED IN</dt>
            <dd>{fmtDateTime(user?.last_login_at)}</dd>
          </div>
          <div>
            <dt>ACCOUNT CREATED</dt>
            <dd>{fmtDate(user?.created_at)}</dd>
          </div>
        </dl>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Session</h2>
          </div>
        </div>
        <div
          className="card-body"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <p
            style={{
              color: "var(--muted)",
              fontSize: 13,
              flex: 1,
              minWidth: 240,
            }}
          >
            Signing out ends this session on this device only. Field devices at
            your godowns stay signed in.
          </p>
          <button className="btn btn-danger" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>

      <EditProfileDrawer
        open={editing}
        firstName={user?.first_name ?? ""}
        lastName={user?.last_name ?? ""}
        mobile={user?.mobile ?? ""}
        avatarUrl={user?.avatar_url ?? null}
        initials={initials}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          setToast("Profile updated.");
          window.setTimeout(() => setToast(null), 3200);
        }}
      />

      <div className={"toast" + (toast ? " show" : "")}>
        <span className="dot" />
        <span>{toast}</span>
      </div>
    </>
  );
}
